import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Article 3 - 壽司 vs 懷石 Omakase
 * SEO 由 ArticlePage.jsx 集中管理，此組件只負責內容渲染
 */
export default function Article3() {
  const comparisonRows = [
    { dim: '料理焦點', sushi: '食材本身的鮮度與品質', kaiseki: '整體用餐體驗的和諧' },
    { dim: '呈現方式', sushi: '簡潔直接，突顯食材', kaiseki: '精緻複雜，追求藝術性' },
    { dim: '用餐節奏', sushi: '相對快速流暢', kaiseki: '緩慢從容，重視儀式感' },
    { dim: '互動程度', sushi: '高度互動，即時交流', kaiseki: '較為安靜，專注品味' },
    { dim: '季節表現', sushi: '透過當季海鮮展現', kaiseki: '全方位的季節敘事' },
    { dim: '器皿選擇', sushi: '相對簡單統一', kaiseki: '每道菜配不同器皿' },
    { dim: '文化根源', sushi: '庶民文化、江戶精神', kaiseki: '茶道、禪宗、貴族文化' },
  ];

  return (
    <div>
      {/* 引言 */}
      <section className="mb-12">
        <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
          在日本料理的世界中，壽司 Omakase 與懷石 Omakase 代表著兩種截然不同的料理哲學與文化體驗。壽司 Omakase 源自江戶時代的庶民文化，強調食材的新鮮與廚師的技藝；懷石 Omakase 則起源於茶道文化，追求禪意、季節感與藝術性的完美結合。
        </p>
      </section>

      {/* 歷史起源 */}
      <section className="mb-16">
        <h2 id="historical-origins" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-blue-500">
          歷史起源與文化背景
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              id: 'sushi-history', icon: '🍣', title: '壽司 Omakase', colorClass: 'red',
              bg: 'from-red-50 to-orange-50 dark:from-gray-800 dark:to-gray-700',
              era: '江戶時代（1603-1868）',
              background: '起源於江戶（現今東京）的街頭小吃文化。最初是為了快速提供食物給忙碌的工人和商人，因此講求速度與效率。',
              concepts: ['新鮮：當日捕撈的海鮮', '簡潔：突顯食材本味', '技藝：廚師的刀工與捏製', '互動：與廚師的即時交流'],
              quote: '「江戶前壽司」原指使用東京灣捕獲的海鮮製作的壽司，後來演變為一種講究新鮮、技藝與速度的料理風格。',
            },
            {
              id: 'kaiseki-history', icon: '🍱', title: '懷石 Omakase', colorClass: 'green',
              bg: 'from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700',
              era: '安土桃山時代（1568-1600）',
              background: '源自茶道文化，由茶聖千利休確立。原本是茶會前提供的簡單餐點，後來發展成精緻的料理藝術。',
              concepts: ['季節：當季食材的極致運用', '美學：器皿、擺盤的藝術性', '禪意：一期一會的精神', '和諧：五味、五色、五法的平衡'],
              quote: '「懷石」原意為「懷中之石」，禪僧在坐禪時會在懷中放溫石以抵禦飢餓，後來演變為茶道中的簡樸料理。',
            },
          ].map(item => (
            <div key={item.id} className={`bg-gradient-to-br ${item.bg} p-8 rounded-2xl`}>
              <h3 id={item.id} className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <span className="text-3xl">{item.icon}</span>{item.title}
              </h3>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <div><h4 className={`font-bold text-${item.colorClass}-600 dark:text-${item.colorClass}-400 mb-2`}>起源時期</h4><p className="text-sm">{item.era}</p></div>
                <div><h4 className={`font-bold text-${item.colorClass}-600 dark:text-${item.colorClass}-400 mb-2`}>文化背景</h4><p className="text-sm">{item.background}</p></div>
                <div>
                  <h4 className={`font-bold text-${item.colorClass}-600 dark:text-${item.colorClass}-400 mb-2`}>核心概念</h4>
                  <ul className="text-sm space-y-1 ml-4">{item.concepts.map(c => <li key={c}>• {c}</li>)}</ul>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl"><p className="text-sm italic">{item.quote}</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 核心哲學比較表 */}
      <section className="mb-16">
        <h2 id="core-philosophy" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-blue-500">
          核心哲學與料理精神
        </h2>
        <div className="overflow-x-auto mb-8">
          <table className="w-full border-collapse bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg">
            <thead>
              <tr className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                <th className="p-4 text-left font-bold">比較維度</th>
                <th className="p-4 text-left font-bold">壽司 Omakase</th>
                <th className="p-4 text-left font-bold">懷石 Omakase</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {comparisonRows.map(row => (
                <tr key={row.dim} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="p-4 font-medium text-gray-900 dark:text-white">{row.dim}</td>
                  <td className="p-4 text-gray-700 dark:text-gray-300">{row.sushi}</td>
                  <td className="p-4 text-gray-700 dark:text-gray-300">{row.kaiseki}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 如何選擇 */}
      <section className="mb-16">
        <h2 id="how-to-choose" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-blue-500">
          壽司 Omakase 與懷石 Omakase，該如何選擇？
        </h2>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {[
            {
              icon: '🍣', title: '選擇壽司 Omakase', colorClass: 'red',
              items: ['喜歡海鮮，特別是生魚片', '享受與廚師互動的過程', '時間有限（1.5-2小時）', '第一次體驗 Omakase', '喜歡簡潔直接的料理風格', '想看到料理的即時製作過程'],
            },
            {
              icon: '🍱', title: '選擇懷石 Omakase', colorClass: 'green',
              items: ['喜歡多樣化的料理方式', '追求完整的文化體驗', '有充足時間（2.5-3.5小時）', '已有 Omakase 經驗', '欣賞器皿與擺盤藝術', '重要場合或特殊慶祝'],
            },
          ].map(card => (
            <div key={card.title} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
              <h3 className={`font-bold text-${card.colorClass}-600 dark:text-${card.colorClass}-400 mb-4 text-xl flex items-center gap-2`}>
                <span className="text-2xl">{card.icon}</span>{card.title}
              </h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300 text-sm">
                {card.items.map(item => (
                  <li key={item} className="flex items-start">
                    <span className={`text-${card.colorClass}-500 mr-2 mt-1`}>✓</span><span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* 結語 */}
      <section className="mb-16">
        <h2 id="conclusion" className="text-3xl font-bold text-gray-900 dark:text-white mb-6">結語</h2>
        <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 p-8 rounded-2xl">
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            壽司 Omakase 與懷石 Omakase 各有其獨特的魅力和文化價值。沒有哪一種絕對優於另一種，選擇取決於您的個人偏好、用餐目的和當下的心境。
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            無論選擇哪一種，都請帶著開放的心態和對料理的尊重。每一次 Omakase 體驗都是獨特的——它不僅是一頓飯，更是一場與廚師的對話、與季節的連結、與文化的交流。
          </p>
        </div>
      </section>

      {/* 延伸閱讀 */}
      <section className="mt-16 pt-8 border-t-2 border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">延伸閱讀</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { to: '/beginners-complete-guide-to-omakase', icon: '📚', title: '初學者的 Omakase 完整指南', desc: '從零開始了解 Omakase 的基礎知識', gradient: 'from-orange-400 to-rose-500', hover: 'orange' },
            { to: '/spring-omakase-sakura-season', icon: '🌸', title: '春季 Omakase 特色', desc: '探索春季料理的獨特魅力', gradient: 'from-pink-400 to-rose-500', hover: 'pink' },
            { to: '/premium-ingredients-analysis-from-tsukiji', icon: '🐟', title: '頂級食材解析', desc: '深入了解 Omakase 使用的頂級食材', gradient: 'from-blue-400 to-cyan-500', hover: 'blue' },
          ].map(link => (
            <Link key={link.to} to={link.to} className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className={`h-48 bg-gradient-to-br ${link.gradient} flex items-center justify-center`}>
                <span className="text-6xl">{link.icon}</span>
              </div>
              <div className="p-6">
                <h3 className={`text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-${link.hover}-500 transition-colors`}>{link.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{link.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}